import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useState, useEffect, useImperativeHandle } from 'react';
import { ChipsGroup, DataGrid, Icon, IconButton, Spacer, useSnackbar } from '@emburse/embark-core';
import { EditIcon } from '@emburse/embark-icons';
import { getUserRecords } from '../../../api/manageusers';
import { ApprovalWorkflowFilter, defaultManageUsersFilterParameters } from '../../../interfaces/ManageUsers';
import { $t, useTranslation } from '../../../hooks/useTranslation';
const ManageUsersApprovalWorkflow = forwardRef(({ onEditUser, missingAccountants, missingApprovers, inactiveApprovers, noApprovalLimit, inheritedAccountant, sameApprover, sameAdditionalApprover, workflowLoading }, ref) => {
    useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const snackbarHandler = useCallback((message, severity) => {
        enqueueSnackbar(message, { variant: 'alert', AlertProps: { severity } });
    }, [enqueueSnackbar]);
    const catchError = useCallback((err) => {
        setLoading(false);
        snackbarHandler(err?.Message || err, 'error');
    }, [snackbarHandler]);
    const defaultFilters = [
        {
            id: ApprovalWorkflowFilter.MissingApprovers,
            label: `${$t('Missing Approvers')} (${missingApprovers})`,
            disabled: !missingApprovers || workflowLoading,
            selected: !!missingApprovers
        },
        {
            id: ApprovalWorkflowFilter.MissingAccountants,
            label: `${$t('Missing Accountants')} (${missingAccountants})`,
            disabled: !missingAccountants ||
                (!!missingAccountants && !missingApprovers) ||
                workflowLoading ||
                loading,
            selected: !!missingAccountants && !missingApprovers
        },
        {
            id: ApprovalWorkflowFilter.InactiveApprovers,
            label: `${$t('Inactive Approvers')} (${inactiveApprovers})`,
            disabled: !inactiveApprovers ||
                (!!inactiveApprovers && !missingApprovers && !missingAccountants) ||
                workflowLoading ||
                loading,
            selected: !!inactiveApprovers && !missingApprovers && !missingAccountants
        },
        {
            id: ApprovalWorkflowFilter.NoApprovalLimit,
            label: `${$t('No Approval Limit')}  (${noApprovalLimit})`,
            disabled: !noApprovalLimit ||
                (!!noApprovalLimit &&
                    !missingApprovers &&
                    !missingAccountants &&
                    !inactiveApprovers) ||
                workflowLoading ||
                loading,
            selected: !!noApprovalLimit &&
                !missingApprovers &&
                !missingAccountants &&
                !inactiveApprovers
        },
        {
            id: ApprovalWorkflowFilter.InheritedAccountant,
            label: `${$t('Inherited Accountant')} (${inheritedAccountant})`,
            disabled: !inheritedAccountant ||
                (!!inheritedAccountant &&
                    !missingApprovers &&
                    !missingAccountants &&
                    !inactiveApprovers &&
                    !noApprovalLimit) ||
                workflowLoading ||
                loading,
            selected: !!inheritedAccountant &&
                !missingApprovers &&
                !missingAccountants &&
                !inactiveApprovers &&
                !noApprovalLimit
        },
        {
            id: ApprovalWorkflowFilter.SameApprover,
            label: `${$t('Users As Own Approver')} (${sameApprover})`,
            disabled: !sameApprover ||
                (!!sameApprover &&
                    !missingApprovers &&
                    !missingAccountants &&
                    !inactiveApprovers &&
                    !noApprovalLimit &&
                    !inheritedAccountant) ||
                workflowLoading ||
                loading,
            selected: !!sameApprover &&
                !missingApprovers &&
                !missingAccountants &&
                !inactiveApprovers &&
                !noApprovalLimit &&
                !inheritedAccountant
        },
        {
            id: ApprovalWorkflowFilter.SameAdditionalApprover,
            label: `${$t('Users As Own Additional Approver')} (${sameAdditionalApprover})`,
            disabled: !sameAdditionalApprover ||
                (!!sameAdditionalApprover &&
                    !missingApprovers &&
                    !missingAccountants &&
                    !inactiveApprovers &&
                    !noApprovalLimit &&
                    !inheritedAccountant &&
                    !sameApprover) ||
                workflowLoading ||
                loading,
            selected: !!sameAdditionalApprover &&
                !missingApprovers &&
                !missingAccountants &&
                !inactiveApprovers &&
                !noApprovalLimit &&
                !inheritedAccountant &&
                !sameApprover
        }
    ];
    const [filters, setFilters] = useState(defaultFilters);
    useEffect(() => {
        if (!workflowLoading) {
            setFilters((prevState) => {
                const newFilters = prevState.map((filter) => {
                    const newFilter = filter;
                    if (filter.id === ApprovalWorkflowFilter.MissingApprovers) {
                        newFilter.label = `${$t('Missing Approvers')} (${missingApprovers})`;
                    }
                    if (filter.id === ApprovalWorkflowFilter.MissingAccountants) {
                        newFilter.label = `${$t('Missing Accountants')} (${missingAccountants})`;
                    }
                    if (filter.id === ApprovalWorkflowFilter.InactiveApprovers) {
                        newFilter.label = `${$t('Inactive Approvers')} (${inactiveApprovers})`;
                    }
                    if (filter.id === ApprovalWorkflowFilter.NoApprovalLimit) {
                        newFilter.label = `${$t('No Approval Limit')}  (${noApprovalLimit})`;
                    }
                    if (filter.id === ApprovalWorkflowFilter.InheritedAccountant) {
                        newFilter.label = `${$t('Inherited Accountant')} (${inheritedAccountant})`;
                    }
                    if (filter.id === ApprovalWorkflowFilter.SameApprover) {
                        newFilter.label = `${$t('Users As Own Approver')} (${sameApprover})`;
                    }
                    if (filter.id === ApprovalWorkflowFilter.SameAdditionalApprover) {
                        newFilter.label = `${$t('Users As Own Additional Approver')} (${sameAdditionalApprover})`;
                    }
                    return newFilter;
                });
                return newFilters;
            });
        }
    }, [
        inactiveApprovers,
        inheritedAccountant,
        missingAccountants,
        missingApprovers,
        noApprovalLimit,
        sameAdditionalApprover,
        sameApprover,
        workflowLoading
    ]);
    const getActiveFilter = useCallback(() => filters.filter((filter) => filter.selected)[0], [filters]);
    const loadUserRecords = (newFilterId) => {
        setLoading(true);
        const filterParameters = {
            ...defaultManageUsersFilterParameters,
            ApprovalWorkflowFilter: newFilterId || getActiveFilter().id
        };
        getUserRecords(filterParameters)
            .then((userData) => {
            let parsedData = JSON.parse(JSON.stringify(userData));
            parsedData = parsedData.map((record) => ({ ...record, id: record.UserPK }));
            setData(parsedData);
        })
            .then(() => setLoading(false))
            .catch(catchError);
    };
    const changeFilter = (chipFilters) => {
        const currentActiveFilter = getActiveFilter();
        const selectedFilter = chipFilters.find((filter) => filter.selected);
        if (currentActiveFilter.id === selectedFilter.id) {
            return;
        }
        const newFilters = chipFilters.map((filter) => {
            const newFilter = filter;
            if (!newFilter.label.includes('(0)')) {
                newFilter.disabled = false;
            }
            if (newFilter.selected) {
                newFilter.disabled = true;
            }
            return newFilter;
        });
        setFilters(newFilters);
        loadUserRecords(selectedFilter.id);
    };
    useEffect(() => {
        if (getActiveFilter()?.id) {
            loadUserRecords();
        }
    }, [catchError, getActiveFilter]);
    useImperativeHandle(ref, () => ({
        loadUserRecords
    }));
    const columns = [
        {
            field: 'UserEmail',
            headerName: $t('Email'),
            flex: 2
        },
        {
            field: 'FirstName',
            headerName: $t('First Name'),
            flex: 2
        },
        {
            field: 'LastName',
            headerName: $t('Last Name'),
            flex: 2
        },
        {
            field: 'ApproverEmail',
            headerName: $t('Approver'),
            flex: 2
        },
        {
            field: 'AdditionalApproverEmail',
            headerName: $t('2nd Approver'),
            flex: 2
        },
        {
            field: 'AccountantEmail',
            headerName: $t('Accountant'),
            flex: 2
        },
        {
            field: 'ActionPlaceholder',
            renderHeader: () => _jsx("span", {}),
            width: 60,
            sortable: false,
            renderCell: (cell) => (_jsx("div", { style: {
                    width: '100%',
                    textAlign: 'center'
                }, children: _jsx(IconButton, { onClick: () => onEditUser(cell.id), children: _jsx(Icon, { iconUrl: EditIcon }) }) }))
        }
    ];
    // hack to make the selected filter not look disabled. Needs to be disabled so it is not clickable
    const selectedFilter = document.getElementsByClassName(`MuiButtonBase-root Mui-disabled MuiChip-root MuiChip-default
        Mui-disabled MuiChip-sizeMedium MuiChip-colorPrimary MuiChip-clickable MuiChip-clickableColorPrimary MuiChip-defaultPrimary
        ChipGroup-selected`)[0];
    if (selectedFilter) {
        selectedFilter.style.opacity = '1';
    }
    return (_jsxs(_Fragment, { children: [_jsx(ChipsGroup, { data: filters, flexItem: true, id: "ApprovalWorkflowFilters", onChange: (filter) => changeFilter(filter.data), variant: "single" }), _jsx(Spacer, { size: 25 }), _jsx(DataGrid, { autoHeight: true, localeText: { noRowsLabel: 'No user records require attention at this time.' }, rows: data, columns: columns, loading: loading })] }));
});
export default ManageUsersApprovalWorkflow;
